import { Injectable, inject } from '@angular/core';
import {
  AdminSmsMessage,
  AdminProviderDeliverySummary,
  AdminMessagePage,
  AdminMessageOptions,
  AdminSmsMessageFilters,
  AdminSmsMessageProfile,
  AdminSmsSendAttempt,
  ClientSmsMessage,
  ClientSmsStats,
  ProviderHistoryMessage,
  CreateSmsTemplateRequest,
  SmsFileRow,
  SmsFileSendResult,
  SmsMultipleSimpleRequest,
  SmsMultipleSimpleResult,
  SmsProviderResponse,
  SmsSendRequest,
  SmsSendResult,
  SmsTemplate,
  SmsTemplateCategory,
  UpdateSmsTemplateRequest
} from '../models/sms.model';
import { SupabaseService } from './supabase.service';

@Injectable({ providedIn: 'root' })
export class SmsService {
  private readonly maxBatchRecipients = 1000;
  private readonly supabase = inject(SupabaseService);

  async sendSingle(request: SmsSendRequest): Promise<SmsSendResult> {
    const recipient = request.recipient ?? request.recipients?.[0] ?? '';

    const { data, error } = await this.supabase.instance.functions.invoke<SmsSendResult>('send-sms', {
      body: {
        recipient,
        message: request.message,
        idempotency_key: request.idempotency_key
      }
    });

    if (error) {
      throw new Error(await this.getFunctionErrorMessage(error));
    }

    if (!data?.success) {
      throw new Error(data?.error || 'No se pudo enviar el SMS.');
    }

    return data;
  }

  async sendBulk(_request: SmsSendRequest): Promise<SmsSendResult> {
    return {
      success: false,
      error: 'Usa sendMultipleSimple para envíos por lote.'
    };
  }

  async sendMultipleSimple(request: SmsMultipleSimpleRequest): Promise<SmsMultipleSimpleResult> {
    if (request.recipients.length > this.maxBatchRecipients) {
      throw new Error(`Máximo ${this.maxBatchRecipients} destinatarios por lote.`);
    }

    const { data, error } = await this.supabase.instance.functions.invoke<SmsMultipleSimpleResult>('send-sms-batch', {
      body: {
        recipients: request.recipients,
        message: request.message,
        idempotency_key: this.createIdempotencyKey()
      }
    });

    if (error) {
      throw new Error(await this.getFunctionErrorMessage(error));
    }

    if (!data) {
      throw new Error('No se recibió respuesta del envío múltiple.');
    }

    return data;
  }

  async sendFileRowsSimple(rows: SmsFileRow[], campaignName = '', key = this.createIdempotencyKey()): Promise<SmsFileSendResult> {
    if (rows.length > 1000) {
      throw new Error('Solo se permiten 1000 registros por lote.');
    }

    const { data, error } = await this.supabase.instance.functions.invoke<SmsMultipleSimpleResult>('send-sms-batch', {
      body: {
        source: 'file',
        campaign_name: campaignName,
        messages: rows.map((row) => ({
          recipient: row.recipient,
          message: row.message
        })),
        idempotency_key: key
      }
    });

    if (error) {
      throw new Error(await this.getFunctionErrorMessage(error));
    }

    if (!data) {
      throw new Error('No se recibió respuesta del envío desde fichero.');
    }

    const sourceByRecipient = new Map(rows.map((row) => [row.recipient, row]));
    const results: SmsFileSendResult['results'] = data.results.map((item) => {
      const source = sourceByRecipient.get(item.recipient);

      return {
        recipient: item.recipient,
        message: source?.message ?? '',
        sourceRow: source?.sourceRow,
        success: item.success,
        message_id: item.message_id,
        status: item.status,
        segments: item.segments,
        cost: item.cost,
        error: item.error || item.error_message || item.provider_message || undefined,
        error_code: item.error_code,
        error_message: item.error_message,
        provider_message: item.provider_message
      };
    });

    const successfulResults = results.filter((result) => result.success);

    return {
      total: rows.length,
      sent: successfulResults.length,
      failed: results.filter((result) => !result.success).length,
      totalSegments: successfulResults.reduce((total, result) => total + Number(result.segments ?? 0), 0),
      totalCost: successfulResults.reduce((total, result) => total + Number(result.cost ?? 0), 0),
      results
    };
  }

  async getCompanyBalance(): Promise<number> {
    const userId = await this.getCurrentUserId();
    const { data, error } = await this.supabase.instance
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      return 0;
    }

    const row = data as Record<string, unknown>;
    const balance = Number(row['credits'] ?? 0);
    return Number.isFinite(balance) ? balance : 0;
  }

  async adminMessageOptions(userId?: string): Promise<AdminMessageOptions> {
    const {data,error}=await this.supabase.instance.rpc('admin_sms_message_options',{p_user_id:userId||null});
    if(error) throw new Error('No se pudieron cargar los filtros. Verifica tus permisos de administrador.');
    return data as AdminMessageOptions;
  }

  async adminMessagePage(filters: AdminSmsMessageFilters): Promise<AdminMessagePage> {
    // Date inputs represent Peru calendar days, with an exclusive next-day upper bound.
    const from=filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00-05:00`).toISOString() : null;
    const to=filters.dateTo ? new Date(Date.parse(`${filters.dateTo}T00:00:00-05:00`)+86400000).toISOString() : null;
    const {data,error}=await this.supabase.instance.rpc('admin_sms_message_page',{
      p_search:filters.search?.trim()||null,p_status:filters.status==='all'?null:filters.status||null,
      p_from:from,p_to:to,p_user_id:filters.userId||null,p_campaign:filters.campaign?.trim()||null,p_page:filters.page||1,p_page_size:filters.pageSize||25
    });
    if(error) throw new Error('No se pudieron cargar los mensajes. Verifica los filtros y tus permisos de administrador.');
    const rows=(data?.messages??[]) as Record<string,unknown>[];
    const messages=rows.map(row=>({...this.mapAdminMessage(row),source:row['source']==='api'?'api' as const:row['source']==='platform'?'platform' as const:null,campaign_id:this.toNullableString(row['campaign_id']),campaign_name:this.toNullableString(row['campaign_name'])}));
    const attempts=await this.listAttemptsForMessages(messages.map(m=>m.id));
    const byId=new Map(attempts.map(a=>[a.sms_message_id,a]));
    const stats=data?.stats??{};
    return {messages:messages.map(m=>({...m,attempt:byId.get(m.id)??null})),stats:{
      expired:Number(stats.expired??0),total:Number(stats.total??0),sent:Number(stats.sent??0),delivered:Number(stats.delivered??0),failed:Number(stats.failed??0),
      consumedSms:Number(stats.consumed_sms??0),totalCost:Number(stats.total_cost??0)
    }};
  }

  async adminProviderDeliverySummary(filters: { userId?: string; dateFrom?: string; dateTo?: string; messageIds?: string[] }): Promise<AdminProviderDeliverySummary> {
    const { data, error } = await this.supabase.instance.functions.invoke<AdminProviderDeliverySummary & { error?: string }>(
      'admin-provider-delivery-summary',
      { body: {
        userId: filters.userId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        messageIds: filters.messageIds?.slice(0, 100),
      } }
    );
    if (error) throw new Error(await this.getFunctionErrorMessage(error));
    if (!data?.success) throw new Error(data?.error || 'No se pudo consultar el resumen del proveedor.');
    return data;
  }

  async listAdminMessages(filters: AdminSmsMessageFilters = {}): Promise<AdminSmsMessage[]> {
    let query = this.supabase.instance
      .from('sms_messages')
      .select(`
        id,
        user_id,
        recipient,
        message,
        segments,
        cost,
        status,
        provider_message_id,
        provider_response,
        error_message,
        sent_at,
        delivered_at,
        created_at,
        profile:profiles (
          full_name,
          email,
          razon_social,
          ruc
        )
      `)
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', `${filters.dateFrom}T00:00:00`);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', `${filters.dateTo}T23:59:59.999`);
    }

    query = query.limit(filters.limit ?? 100);

    const { data, error } = await query;

    if (error) {
      throw new Error('No se pudieron cargar los mensajes. Verifica permisos de administrador.');
    }

    const messages = ((data as unknown[]) ?? []).map((item) => this.mapAdminMessage(item));
    const attempts = await this.listAttemptsForMessages(messages.map((message) => message.id));
    const attemptsByMessageId = new Map(
      attempts
        .filter((attempt) => attempt.sms_message_id)
        .map((attempt) => [attempt.sms_message_id as string, attempt])
    );

    return messages.map((message) => ({
      ...message,
      attempt: attemptsByMessageId.get(message.id) ?? null
    }));
  }

  async listMyMessages(limit = 100): Promise<ClientSmsMessage[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await this.supabase.instance
      .from('sms_messages')
      .select(`
        id,
        user_id,
        recipient,
        message,
        segments,
        cost,
        status,
        provider_message_id,
        provider_response,
        error_message,
        sent_at,
        delivered_at,
        created_at
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error('No se pudo cargar tu historial de SMS.');
    }

    const messages = ((data as unknown[]) ?? []).map((item) => this.mapClientMessage(item));
    const attempts = await this.listAttemptsForMessages(messages.map((message) => message.id));
    const attemptsByMessageId = new Map(
      attempts
        .filter((attempt) => attempt.sms_message_id)
        .map((attempt) => [attempt.sms_message_id as string, attempt])
    );

    return messages.map((message) => ({
      ...message,
      attempt: attemptsByMessageId.get(message.id) ?? null
    }));
  }

  async getRecentMyMessages(limit = 5): Promise<ClientSmsMessage[]> {
    return this.listMyMessages(limit);
  }

  async listMyHistory(filters: { from: string; to: string; phone: string; status: string; page: number; pageSize: number }): Promise<{ messages: ClientSmsMessage[]; total: number }> {
    const userId = await this.getCurrentUserId();
    let query = this.supabase.instance.from('sms_messages')
      .select('id,user_id,recipient,message,segments,cost,status,provider_message_id,provider_response,error_message,sent_at,delivered_at,created_at', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', filters.from).lte('created_at', filters.to)
      .order('created_at', { ascending: false }).order('id', { ascending: false });
    const phone = filters.phone.replace(/\D/g, '');
    if (phone) query = query.ilike('recipient', `%${phone}%`);
    if (filters.status !== 'all') query = query.eq('status', filters.status);
    const { data, error, count } = await query.range((filters.page - 1) * filters.pageSize, filters.page * filters.pageSize - 1);
    if (error) throw new Error('No se pudo cargar tu historial de SMS. Inténtalo nuevamente.');
    return { messages: (data ?? []).map((row) => this.mapClientMessage(row)), total: count ?? 0 };
  }

  async consultProviderHistory(): Promise<ProviderHistoryMessage[]> {
    const { data: sessionData } = await this.supabase.instance.auth.getSession();
    if (!sessionData.session) throw new Error('Sesión inválida.');
    const { data, error } = await this.supabase.instance.functions.invoke('client-sms-history', {
      headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      body: {}
    });
    if (error) throw new Error(await this.getFunctionErrorMessage(error));
    if (!data?.success) throw new Error(data?.error || 'No se pudo consultar el historial del proveedor.');
    if (!Array.isArray(data.data)) throw new Error('Formato inválido del historial del proveedor.');
    return data.data as ProviderHistoryMessage[];
  }

  async getMySmsStats(): Promise<ClientSmsStats> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await this.supabase.instance
      .from('sms_messages')
      .select('status, segments, cost')
      .eq('user_id', userId);

    if (error) {
      throw new Error('No se pudo cargar tu historial de SMS.');
    }

    const rows = ((data as unknown[]) ?? []).map((item) => {
      const row = item as Record<string, unknown>;
      return {
        status: this.toMessageStatus(row['status']),
        segments: Number(row['segments'] ?? 0),
        cost: Number(row['cost'] ?? 0)
      };
    });
    const billableMessages = rows.filter((message) => message.status === 'sent' || message.status === 'delivered');

    return {
      total: rows.length,
      sent: rows.filter((message) => message.status === 'sent').length,
      delivered: rows.filter((message) => message.status === 'delivered').length,
      failed: rows.filter((message) => message.status === 'failed').length,
      pending: rows.filter((message) => message.status === 'pending').length,
      consumedSegments: billableMessages.reduce((total, message) => total + message.segments, 0),
      totalCost: billableMessages.reduce((total, message) => total + message.cost, 0)
    };
  }

  async uploadCampaign(_file: File): Promise<void> {
    // TODO: implement file upload flow.
  }

  extractTemplateVariables(content: string): string[] {
    const matches = content.matchAll(/\{\s*([a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ-]+)\s*\}/g);
    return Array.from(new Set(Array.from(matches, (match) => match[1]?.trim()).filter(Boolean)));
  }

  renderTemplatePreview(content: string, values: Record<string, string>): string {
    return content.replace(/\{\s*([a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ-]+)\s*\}/g, (match, variableName: string) => {
      const value = values[variableName.trim()]?.trim();
      return value || match;
    });
  }

  renderTemplateExample(content: string): string {
    return content.replace(/\{\s*([a-zA-Z0-9_áéíóúÁÉÍÓÚñÑ-]+)\s*\}/g, (_match, variableName: string) =>
      this.getExampleValue(variableName.trim())
    );
  }

  getExampleValue(variableName: string): string {
    const normalized = variableName.trim().toLowerCase();
    const examples: Record<string, string> = {
      nombre: 'Juan',
      codigo: '123456',
      empresa: 'Fortuna',
      fecha: '02/05/2026',
      monto: 'S/ 50.00'
    };

    return examples[normalized] ?? 'Ejemplo';
  }

  calculateSegments(message: string): number {
    return Math.ceil(message.length / 160) || 1;
  }

  async listTemplates(): Promise<SmsTemplate[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await this.supabase.instance
      .from('templates')
      .select('id, user_id, name, content, category, variables, is_active, created_at, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      throw new Error('No se pudieron cargar las plantillas.');
    }

    return ((data as unknown[]) ?? []).map((item) => this.mapTemplate(item));
  }

  async listActiveTemplates(): Promise<SmsTemplate[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await this.supabase.instance
      .from('templates')
      .select('id, user_id, name, content, category, variables, is_active, created_at, updated_at')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) {
      throw new Error('No se pudieron cargar las plantillas.');
    }

    return ((data as unknown[]) ?? []).map((item) => this.mapTemplate(item));
  }

  async getTemplate(id: string): Promise<SmsTemplate | null> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await this.supabase.instance
      .from('templates')
      .select('id, user_id, name, content, category, variables, is_active, created_at, updated_at')
      .eq('id', id)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      throw new Error('No se pudo cargar la plantilla.');
    }

    return data ? this.mapTemplate(data) : null;
  }

  async createTemplate(payload: CreateSmsTemplateRequest): Promise<SmsTemplate> {
    const userId = await this.getCurrentUserId();
    const content = payload.content.trim();

    const { data, error } = await this.supabase.instance
      .from('templates')
      .insert({
        user_id: userId,
        name: payload.name.trim(),
        content,
        category: payload.category,
        variables: this.extractTemplateVariables(content)
      })
      .select('id, user_id, name, content, category, variables, is_active, created_at, updated_at')
      .single();

    if (error) {
      throw new Error('No se pudo guardar la plantilla.');
    }

    return this.mapTemplate(data);
  }

  async updateTemplate(id: string, payload: UpdateSmsTemplateRequest): Promise<SmsTemplate> {
    const userId = await this.getCurrentUserId();
    const updatePayload: Record<string, unknown> = {
      updated_at: new Date().toISOString()
    };

    if (payload.name !== undefined) updatePayload['name'] = payload.name.trim();
    if (payload.content !== undefined) {
      const content = payload.content.trim();
      updatePayload['content'] = content;
      updatePayload['variables'] = this.extractTemplateVariables(content);
    }
    if (payload.category !== undefined) updatePayload['category'] = payload.category;
    if (payload.variables !== undefined && payload.content === undefined) updatePayload['variables'] = payload.variables;
    if (payload.is_active !== undefined) updatePayload['is_active'] = payload.is_active;

    const { data, error } = await this.supabase.instance
      .from('templates')
      .update(updatePayload)
      .eq('id', id)
      .eq('user_id', userId)
      .select('id, user_id, name, content, category, variables, is_active, created_at, updated_at')
      .single();

    if (error) {
      throw new Error('No se pudo guardar la plantilla.');
    }

    return this.mapTemplate(data);
  }

  async deleteTemplate(id: string): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await this.supabase.instance
      .from('templates')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      throw new Error('No se pudo eliminar la plantilla.');
    }
  }

  private async getFunctionErrorMessage(error: unknown): Promise<string> {
    const context = (error as { context?: unknown }).context;

    if (context instanceof Response) {
      try {
        const payload = await context.clone().json() as { error?: string; message?: string };
        return payload.error || payload.message || 'No se pudo enviar el SMS.';
      } catch {
        return 'No se pudo enviar el SMS.';
      }
    }

    return error instanceof Error
      ? error.message
      : 'No se pudo enviar el SMS.';
  }

  private createIdempotencyKey(): string {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }

    const values = new Uint32Array(4);
    globalThis.crypto?.getRandomValues(values);
    const randomPart = Array.from(values, (value) => value.toString(36)).join('');
    return `sms_${Date.now().toString(36)}_${randomPart}`;
  }

  private isSessionError(message: string): boolean {
    const normalized = message.toLowerCase();
    return normalized.includes('sesión') ||
      normalized.includes('session') ||
      normalized.includes('not_authorized') ||
      normalized.includes('no tienes permisos');
  }

  private mapAdminMessage(item: unknown): AdminSmsMessage {
    const row = item as Record<string, unknown>;

    return {
      id: String(row['id'] ?? ''),
      user_id: String(row['user_id'] ?? ''),
      recipient: String(row['recipient'] ?? ''),
      message: String(row['message'] ?? ''),
      segments: Number(row['segments'] ?? 0),
      cost: Number(row['cost'] ?? 0),
      status: row['status'] === 'expired' ? 'expired' : this.toMessageStatus(row['status']),
      provider_message_id: this.toNullableString(row['provider_message_id']),
      provider_response: this.toProviderResponse(row['provider_response']),
      error_message: this.toNullableString(row['error_message']),
      sent_at: this.toNullableString(row['sent_at']),
      delivered_at: this.toNullableString(row['delivered_at']),
      created_at: String(row['created_at'] ?? ''),
      profile: this.toProfile(row['profile']),
      attempt: null
    };
  }

  private mapClientMessage(item: unknown): ClientSmsMessage {
    const row = item as Record<string, unknown>;

    return {
      id: String(row['id'] ?? ''),
      user_id: String(row['user_id'] ?? ''),
      recipient: String(row['recipient'] ?? ''),
      message: String(row['message'] ?? ''),
      segments: Number(row['segments'] ?? 0),
      cost: Number(row['cost'] ?? 0),
      status: this.toMessageStatus(row['status']),
      provider_message_id: this.toNullableString(row['provider_message_id']),
      provider_response: this.toProviderResponse(row['provider_response']),
      error_message: this.toNullableString(row['error_message']),
      sent_at: this.toNullableString(row['sent_at']),
      delivered_at: this.toNullableString(row['delivered_at']),
      created_at: String(row['created_at'] ?? ''),
      attempt: null
    };
  }

  private async listAttemptsForMessages(messageIds: string[]): Promise<AdminSmsSendAttempt[]> {
    if (messageIds.length === 0) {
      return [];
    }

    const { data, error } = await this.supabase.instance
      .from('sms_send_attempts')
      .select(`
        id,
        sms_message_id,
        idempotency_key,
        provider_recipient,
        status,
        provider,
        provider_response,
        error_message,
        started_at,
        completed_at,
        expires_at,
        created_at,
        updated_at
      `)
      .in('sms_message_id', messageIds);

    if (error) {
      return [];
    }

    return ((data as unknown[]) ?? []).map((item) => this.mapAdminSmsAttempt(item));
  }

  private mapAdminSmsAttempt(item: unknown): AdminSmsSendAttempt {
    const row = item as Record<string, unknown>;

    return {
      id: String(row['id'] ?? ''),
      sms_message_id: this.toNullableString(row['sms_message_id']),
      idempotency_key: String(row['idempotency_key'] ?? ''),
      provider_recipient: this.toNullableString(row['provider_recipient']),
      status: this.toAttemptStatus(row['status']),
      provider: this.toNullableString(row['provider']),
      provider_response: this.toProviderResponse(row['provider_response']),
      error_message: this.toNullableString(row['error_message']),
      started_at: this.toNullableString(row['started_at']),
      completed_at: this.toNullableString(row['completed_at']),
      expires_at: this.toNullableString(row['expires_at']),
      created_at: String(row['created_at'] ?? ''),
      updated_at: this.toNullableString(row['updated_at'])
    };
  }

  private async getCurrentUserId(): Promise<string> {
    const { data, error } = await this.supabase.instance.auth.getSession();
    const userId = data.session?.user?.id;

    if (error || !userId) {
      throw new Error('Sesión inválida.');
    }

    return userId;
  }

  private mapTemplate(item: unknown): SmsTemplate {
    const row = item as Record<string, unknown>;

    return {
      id: String(row['id'] ?? ''),
      user_id: String(row['user_id'] ?? ''),
      name: String(row['name'] ?? ''),
      content: String(row['content'] ?? ''),
      category: this.toTemplateCategory(row['category']),
      variables: this.toStringArray(row['variables']),
      is_active: row['is_active'] !== false,
      created_at: String(row['created_at'] ?? ''),
      updated_at: String(row['updated_at'] ?? row['created_at'] ?? '')
    };
  }

  private toTemplateCategory(value: unknown): SmsTemplateCategory {
    return value === 'general' ||
      value === 'marketing' ||
      value === 'cobranza' ||
      value === 'recordatorio' ||
      value === 'soporte' ||
      value === 'otro'
      ? value
      : 'general';
  }

  private toMessageStatus(value: unknown): ClientSmsMessage['status'] {
    return value === 'pending' || value === 'sent' || value === 'delivered' || value === 'failed'
      ? value
      : 'pending';
  }

  private toAttemptStatus(value: unknown): AdminSmsSendAttempt['status'] {
    return value === 'processing' || value === 'sent' || value === 'failed'
      ? value
      : 'processing';
  }

  private toProviderResponse(value: unknown): SmsProviderResponse | null {
    return value && typeof value === 'object'
      ? value as SmsProviderResponse
      : null;
  }

  private toProfile(value: unknown): AdminSmsMessageProfile | null {
    const profile = Array.isArray(value) ? value[0] : value;

    if (!profile || typeof profile !== 'object') {
      return null;
    }

    const data = profile as Record<string, unknown>;

    return {
      full_name: this.toNullableString(data['full_name']),
      email: this.toNullableString(data['email']),
      razon_social: this.toNullableString(data['razon_social']),
      ruc: this.toNullableString(data['ruc'])
    };
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === 'string' && value.trim()
      ? value
      : null;
  }

  private toStringArray(value: unknown): string[] {
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : [];
  }
}
